import { PopoverPanel } from "@headlessui/react";
import { ActionItemId, actionItemStatus, Relations, UserId } from "@inrev/common";
import { useCreateActionItem, useUpdateActionItem } from "@portal/api/action-item/admin";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { Button, DatePicker } from "@portal/components/primitives";
import { CheckMarkBubble } from "@portal/components/ui/CheckMarkBubble";
import { Menu } from "@portal/components/ui/Menu";
import { RichTextEditor } from "@portal/components/ui/RichTextEditor";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { DateTime } from "luxon";
import { useState } from "react";
import { assigneeUserFromQuery } from "../../../AdminAssigneeControls";
import { AdminActionItemLayout } from "../layouts/AdminActionItemLayout";
import { ActionItemSelectAssignee } from "./ActionItemSelectAssignee";
import { ActionItemTitleInput } from "./ActionItemTitleInput";

export type CreateOrUpdateActionItemProps = {
	relation: Relations.ActionItems;
	defaultUnderwriterId: UserId;
	inputDefaults?: {
		title?: string;
		content?: string | null;
		dueAt?: DateTime | null;
		assigneeId?: UserId;
		isComplete?: boolean;
	};
	isUpdate?: boolean;
	onClose?: () => void;
} & (
	| {
			isUpdate: true;
			actionItemId: ActionItemId;
	  }
	| {
			isUpdate?: false;
			actionItemId?: never;
	  }
);

export const CreateOrUpdateActionItem = ({
	relation,
	defaultUnderwriterId,
	onClose,
	inputDefaults,
	isUpdate,
	actionItemId,
}: CreateOrUpdateActionItemProps) => {
	const { user } = useAuthenticatedUser();
	const { createActionItem } = useCreateActionItem();
	const { updateActionItem } = useUpdateActionItem();
	const { adminUsers = [] } = useAdminFetchAdminUsers();
	const [isComplete, toggleComplete] = useState(inputDefaults?.isComplete || false);
	const [title, setTitle] = useState(inputDefaults?.title || "");
	const [actionItemContent, setActionItemContent] = useState(inputDefaults?.content);
	const [dueAt, setDueAt] = useState<DateTime>(inputDefaults?.dueAt || DateTime.now());
	const [assigneeId, setAssigneeId] = useState<UserId>(
		inputDefaults?.assigneeId || defaultUnderwriterId,
	);
	const [errors, setErrors] = useState<{ title?: string }>({});

	const createFn = () => {
		const inputErrors = validateInput({ title });
		if (Object.keys(inputErrors).length > 0) {
			setErrors(inputErrors);
			return;
		}

		const actionItem = {
			title,
			content: actionItemContent,
			status: isComplete ? actionItemStatus.complete : actionItemStatus.open,
			ownerId: user.id,
			assigneeId: assigneeId,
			dueAt: dueAt,
			completedByUserId: isComplete ? assigneeId : undefined,
			completedAt: isComplete ? DateTime.now() : undefined,
			...relation,
		};

		createActionItem(actionItem);
		onClose && onClose();
	};

	const updateFn = () => {
		if (!actionItemId) return;
		const inputErrors = validateInput({ title });
		if (Object.keys(inputErrors).length > 0) {
			setErrors(inputErrors);
			return;
		}

		const actionItem = {
			...(title !== inputDefaults?.title && { title }),
			...(isComplete !== inputDefaults?.isComplete && {
				status: isComplete ? actionItemStatus.complete : actionItemStatus.open,
				completedByUserId: isComplete ? assigneeId : undefined,
				completedAt: isComplete ? DateTime.now() : undefined,
			}),
			...(actionItemContent !== inputDefaults?.content && { content: actionItemContent }),
			...(assigneeId !== inputDefaults?.assigneeId && { assigneeId }),
			...(dueAt !== inputDefaults?.dueAt && { dueAt }),
		};

		if (Object.keys(actionItem).length !== 0) {
			updateActionItem({ actionItemId, updatedFields: actionItem, relation });
		}

		onClose && onClose();
	};

	const validateInput = ({ title }: { title?: string }) => {
		const errors: { title?: string } = {};
		if (!title || title.length === 0) errors.title = "Title is required";
		return errors;
	};

	const footer = (
		<div className="min-w-full flex items-center justify-between flex-col gap-4 mt-5">
			<div className="min-w-full">
				<RichTextEditor
					collapsedPlaceholder={isUpdate ? undefined : "Add Content"}
					content={inputDefaults?.content || ""}
					onUpdate={setActionItemContent}
				/>
			</div>
			<div className="flex flex-row min-w-full gap-3">
				<Button
					data-testid="action-item-submit-button"
					color="primary"
					onClick={isUpdate ? updateFn : createFn}
				>
					{isUpdate ? "Update" : "Create"}
				</Button>
				<Button data-testid="action-item-cancel-button" color="secondary" onClick={onClose}>
					Cancel
				</Button>
			</div>
		</div>
	);

	const content = (
		<>
			<div className="flex flex-col justify-start gap-2 flex-grow">
				<ActionItemTitleInput updateFn={setTitle} title={title} error={errors.title} />
				<div data-testid="action-item-due-date" className="flex items-center gap-2">
					<div className="text-[12px] text-tertiary pt-[2px]">Due: </div>
					<Menu
						button={
							<div className="text-tertiary text-[12px] font-[450] underline cursor-pointer hover:text-gray-800">
								{dueAt.toFormat("LLL dd, yyyy")}
							</div>
						}
						anchor="top"
					>
						<PopoverPanel>
							{({ close }) => (
								<DatePicker
									value={dueAt.toJSDate()}
									defaultMonth={DateTime.now().toJSDate()}
									onSelect={(day) => {
										if (day) setDueAt(DateTime.fromJSDate(day));
										close();
									}}
								/>
							)}
						</PopoverPanel>
					</Menu>
				</div>
			</div>
			<div className="flex items-center justify-center self-start min-w-fit">
				<ActionItemSelectAssignee
					defaultUnderwriterId={defaultUnderwriterId}
					userList={adminUsers.map(assigneeUserFromQuery)}
					selectedUserId={assigneeId}
					setUserId={setAssigneeId}
				/>
			</div>
		</>
	);

	return (
		<AdminActionItemLayout
			testId="admin-action-item-create-section-layout"
			containerClassName={isUpdate ? "border-b-0" : ""}
			statusIcon={
				<CheckMarkBubble
					isComplete={isComplete}
					toggleComplete={() => toggleComplete(!isComplete)}
				/>
			}
			content={content}
			footer={footer}
		/>
	);
};
